import { createActionCreator, createReducer } from "deox";
import {
  AboutUsConfiguration,
  ContactInformation,
  DefaultGenericDiamondConfiguration,
  HomepageConfiguration,
  Support,
  UpsertAboutUsConfiguration,
  UpsertImagesConfiguration,
} from "../../models/misc";
import * as contactService from "../../services/contact";
import * as siteSettingsService from "../../services/siteSettings";
import { dataUrlToBase64 } from "../../utils";
import { withErrorDispatch } from "./withErrorDispatch";

export type State = {
  homepage: HomepageConfiguration | null;
  aboutUs: AboutUsConfiguration | null;
  contactInformation: ContactInformation | null;
  maintenance: boolean | null;
  maintenanceMessage: string | null;
  support: Support | null;
  defaultGenericDiamondConfiguration: DefaultGenericDiamondConfiguration | null;
};

const fetchHomepageConfiguration = Object.assign(
  () =>
    withErrorDispatch(
      siteSettingsService.getHomepageConfiguration(),
      result => fetchHomepageConfiguration.success(result),
      "Error fetching homepage configuration"
    ),
  {
    success: createActionCreator("@@ADMIN/HOMEPAGE/FETCH", resolve => (item: HomepageConfiguration) => resolve(item)),
  }
);

const fetchDefaultGenericDiamondConfiguration = Object.assign(
  () =>
    withErrorDispatch(
      siteSettingsService.getDefaultGenericDiamondConfiguration(),
      result => fetchDefaultGenericDiamondConfiguration.success(result),
      "Error fetching default generic diamond configuration"
    ),
  {
    success: createActionCreator(
      "@@ADMIN/DEFAULT_GENERIC_DIAMOND/FETCH",
      resolve => (item: DefaultGenericDiamondConfiguration) => resolve(item)
    ),
  }
);

const updateDefaultGenericDiamondConfiguration = Object.assign(
  (settings: DefaultGenericDiamondConfiguration) => {
    return withErrorDispatch(
      siteSettingsService.updateDefaultGenericDiamondConfiguration(settings),
      result => updateDefaultGenericDiamondConfiguration.success(result),
      "Error saving default generic diamond configuration us configuration"
    );
  },
  {
    success: createActionCreator(
      "@@ADMIN/DEFAULT_GENERIC_DIAMOND/UPDATE",
      resolve => (item: DefaultGenericDiamondConfiguration) => resolve(item)
    ),
  }
);



const fetchAboutUsConfiguration = Object.assign(
  () =>
    withErrorDispatch(
      siteSettingsService.getAboutUsConfiguration(),
      result => fetchAboutUsConfiguration.success(result),
      "Error fetching about us configuration"
    ),
  {
    success: createActionCreator("@@ADMIN/ABOUT_US/FETCH", resolve => (item: AboutUsConfiguration) => resolve(item)),
  }
);

const fetchContactInformation = Object.assign(
  () =>
    withErrorDispatch(
      contactService.getContactInformation(),
      result => fetchContactInformation.success(result),
      "Error fetching contact information"
    ),
  {
    success: createActionCreator("@@ADMIN/CONTACT/FETCH", resolve => (item: ContactInformation) => resolve(item)),
  }
);

const fetchMaintenance = Object.assign(
  () =>
    withErrorDispatch(
      siteSettingsService.getMaintenance(),
      result => fetchMaintenance.success(result),
      "Error fetching maintenanance information"
    ),
  {
    success: createActionCreator("@@ADMIN/MAINTENANCE/FETCH", resolve => (item: boolean) => resolve(item)),
  }
);

const fetchMaintenanceMessage = Object.assign(
  () =>
    withErrorDispatch(
      siteSettingsService.getMaintenanceMessage(),
      result => fetchMaintenanceMessage.success(result),
      "Error fetching maintenanance information"
    ),
  {
    success: createActionCreator("@@ADMIN/MAINTENANCE_MESSAGE/FETCH", resolve => (item: string) => resolve(item)),
  }
);

const setMaintenance = Object.assign(
  (isEnabled: boolean) =>
    withErrorDispatch(
      siteSettingsService.setMaintenance(isEnabled),
      () => setMaintenance.success(isEnabled),
      "Error updating maintenance information"
    ),
  {
    success: createActionCreator("@@ADMIN/MAINTENANCE/SET", resolve => (item: boolean) => resolve(item)),
  }
);

const setMaintenanceMessage = Object.assign(
  (message: string) =>
    withErrorDispatch(
      siteSettingsService.setMaintenanceMessage(message),
      () => setMaintenanceMessage.success(message),
      "Error updating maintenance information"
    ),
  {
    success: createActionCreator("@@ADMIN/MAINTENANCE_MESSAGE/SET", resolve => (item: string) => resolve(item)),
  }
);

const updateHomepageConfiguration = Object.assign(
  (settings: UpsertImagesConfiguration) => {
    settings.homepageImage?.content && (settings.homepageImage.content = dataUrlToBase64(settings.homepageImage.content));
    settings.cardRings?.content && (settings.cardRings.content = dataUrlToBase64(settings.cardRings.content));
    settings.cardBracelets?.content &&
      (settings.cardBracelets.content = dataUrlToBase64(settings.cardBracelets.content));
    settings.cardNecklaces?.content &&
      (settings.cardNecklaces.content = dataUrlToBase64(settings.cardNecklaces.content));
    settings.cardEarrings?.content && (settings.cardEarrings.content = dataUrlToBase64(settings.cardEarrings.content));

    settings.aboutImage?.content && (settings.aboutImage.content = dataUrlToBase64(settings.aboutImage.content));
    settings.slider1Image?.content && (settings.slider1Image.content = dataUrlToBase64(settings.slider1Image.content));
    settings.slider2Image?.content && (settings.slider2Image.content = dataUrlToBase64(settings.slider2Image.content));
    settings.slider3Image?.content && (settings.slider3Image.content = dataUrlToBase64(settings.slider3Image.content));
    
    settings.e1Video?.content && (settings.e1Video.content = dataUrlToBase64(settings.e1Video.content));
    settings.e2Video?.content && (settings.e2Video.content = dataUrlToBase64(settings.e2Video.content));
    settings.e3Video?.content && (settings.e3Video.content = dataUrlToBase64(settings.e3Video.content));




    return withErrorDispatch(
      siteSettingsService.updateHomepageConfiguration(settings),
      result => updateHomepageConfiguration.success(result),
      "Error saving homepage configuration"
    );
  },
  {
    success: createActionCreator("@@ADMIN/HOMEPAGE/UPDATE", resolve => (item: HomepageConfiguration) => resolve(item)),
  }
);

const updateAboutUsConfiguration = Object.assign(
  (settings: UpsertAboutUsConfiguration) => {
    settings.image1?.content && (settings.image1.content = dataUrlToBase64(settings.image1.content));
    settings.image2?.content && (settings.image2.content = dataUrlToBase64(settings.image2.content));

    return withErrorDispatch(
      siteSettingsService.updateAboutUsConfiguration(settings),
      result => updateAboutUsConfiguration.success(result),
      "Error saving about us configuration"
    );
  },
  {
    success: createActionCreator("@@ADMIN/ABOUT_US/UPDATE", resolve => (item: AboutUsConfiguration) => resolve(item)),
  }
);

const updateContactInformation = Object.assign(
  (information: ContactInformation) =>
    withErrorDispatch(
      contactService.updateContactInformation(information),
      () => updateContactInformation.success(information),
      "Error updating contact information"
    ),
  {
    success: createActionCreator("@@ADMIN/CONTACT/UPDATE", resolve => (item: ContactInformation) => resolve(item)),
  }
);

const fetchSupportInformation = Object.assign(
  () =>
    withErrorDispatch(
      contactService.getSupportInfo(),
      result => fetchSupportInformation.success(result),
      "Error fetching support contacts"
    ),
  {
    success: createActionCreator("@@ADMIN/SUPPORT/FETCH", resolve => (item: Support) => resolve(item)),
  }
);

const updateSupportInformation = Object.assign(
  (information: Support) =>
    withErrorDispatch(
      contactService.updateSupportInfo(information),
      () => updateSupportInformation.success(information),
      "Error updating support contacts"
    ),
  {
    success: createActionCreator("@@ADMIN/SUPPORT/UPDATE", resolve => (information: Support) => resolve(information)),
  }
);

const defaultState: State = {
  homepage: null,
  aboutUs: null,
  contactInformation: null,
  maintenance: false,
  maintenanceMessage: null,
  support: null,
  defaultGenericDiamondConfiguration: null,

};

const reducer = createReducer(defaultState, handleAction => [
  handleAction([fetchHomepageConfiguration.success, updateHomepageConfiguration.success], (state, action) => ({
    ...state,
    homepage: action.payload,
  })),
  handleAction([fetchAboutUsConfiguration.success, updateAboutUsConfiguration.success], (state, action) => ({
    ...state,
    aboutUs: action.payload,
  })),
  handleAction([fetchContactInformation.success, updateContactInformation.success], (state, action) => ({
    ...state,
    contactInformation: action.payload,
  })),
  handleAction(
    [fetchDefaultGenericDiamondConfiguration.success, updateDefaultGenericDiamondConfiguration.success],
    (state, action) => ({
      ...state,
      defaultGenericDiamondConfiguration: action.payload,
    })
  ),
  handleAction([fetchMaintenance.success, setMaintenance.success], (state, action) => ({
    ...state,
    maintenance: action.payload,
  })),
  handleAction([fetchMaintenanceMessage.success, setMaintenanceMessage.success], (state, action) => ({
    ...state,
    maintenanceMessage: action.payload,
  })),
  handleAction([fetchSupportInformation.success, updateSupportInformation.success], (state, action) => ({
    ...state,
    support: action.payload,
  })),
]);

const actions = {
  fetchHomepageConfiguration,
  fetchAboutUsConfiguration,
  fetchContactInformation,
  fetchMaintenance,
  fetchMaintenanceMessage,
  setMaintenance,
  setMaintenanceMessage,
  updateHomepageConfiguration,
  updateAboutUsConfiguration,
  updateContactInformation,
  updateSupportInformation,
  fetchSupportInformation,
  fetchDefaultGenericDiamondConfiguration,
  updateDefaultGenericDiamondConfiguration,
};

export { actions, reducer };
